// The TODAY KPI STRIP (Today Screen v2, the "Signal" re-layout): one mini-card per Metric the shared wire
// carries — the awaiting dot + mono name, the latest figure in the voice face, the FACTUAL last-two-values
// delta with its direction arrow (the same direction-tone vocabulary TrendArrow already fixes — G-106
// keeps judgment direction-only), the sparkline over the recorded series, and one stated note line (the
// target gap and/or the awaiting state — never a good/bad verdict). Every figure derives from the
// projection (G-182); with fewer than two recorded points no sparkline draws and no delta shows (P-SELF).
// Clicking a card OPENS the KPIs board (in-app hash navigation, F10). Tokens only (G-181).

import type { MetricCard } from '../projections.ts';
import { Sparkline } from './Sparkline.tsx';
import { TrendArrow } from '../primitives.tsx';
import {
  fmtMetricValue,
  fmtMetricDelta,
  fmtTargetGap,
  isAwaiting,
  metricStatusLine,
} from './metric-format.ts';

/** The card's one note line — the stated target gap / lifecycle fact, amber only when awaiting a human. */
function noteFor(metric: MetricCard): { readonly text: string; readonly pending: boolean } {
  const awaiting = isAwaiting(metric);
  if (metric.state !== 'active') return { text: metricStatusLine(metric), pending: awaiting };
  const value = metric.latestValue;
  if (value === null) return { text: 'no values yet', pending: false };
  const gap =
    metric.target === null
      ? 'no target set'
      : fmtTargetGap(value.value, metric.target) === 'at target'
        ? 'at target'
        : `${fmtTargetGap(value.value, metric.target)} target`;
  return { text: awaiting ? `${gap} · awaiting you` : gap, pending: awaiting };
}

/** The factual direction of the last two recorded values, for the delta's direction tone. */
function deltaDirection(metric: MetricCard): 'up' | 'down' | 'flat' | null {
  if (metric.series.length < 2) return null;
  const last = metric.series[metric.series.length - 1]!.value;
  const prev = metric.series[metric.series.length - 2]!.value;
  return last > prev ? 'up' : last < prev ? 'down' : 'flat';
}

/** One Today KPI mini-card — a whole-card control that opens the KPIs board. */
function StripCard({
  metric,
  onOpenKpis,
}: {
  readonly metric: MetricCard;
  readonly onOpenKpis: () => void;
}): React.JSX.Element {
  const awaiting = isAwaiting(metric);
  const delta = fmtMetricDelta(metric.series);
  const direction = deltaDirection(metric);
  const note = noteFor(metric);
  return (
    <button
      type="button"
      className="tk-card"
      data-metric={metric.id}
      onClick={onOpenKpis}
      aria-label={`Open KPIs: ${metric.name}`}
    >
      <span className="tk-id">
        <span className={awaiting ? 'dot pending' : 'dot'} aria-hidden="true" />
        <span className="tk-name">{metric.name}</span>
      </span>
      <span className="tk-headline">
        <span className="tk-now">
          {metric.latestValue !== null ? fmtMetricValue(metric.latestValue.value) : '—'}
        </span>
        {delta !== null && direction !== null ? (
          <span className="tk-delta" data-dir={direction}>
            <TrendArrow direction={direction} />
            {delta}
          </span>
        ) : null}
      </span>
      {metric.series.length >= 2 ? (
        <span className="tk-spark">
          <Sparkline series={metric.series} />
        </span>
      ) : null}
      <span className={note.pending ? 'tk-note pending' : 'tk-note'}>{note.text}</span>
    </button>
  );
}

/** The Today KPI strip — one mini-card per Metric; renders nothing when none are tracked yet. */
export function TodayKpiStrip({
  metrics,
  onOpenKpis,
}: {
  readonly metrics: readonly MetricCard[];
  readonly onOpenKpis: () => void;
}): React.JSX.Element | null {
  if (metrics.length === 0) return null;
  return (
    <div className="today-kpis" aria-label="KPIs to watch">
      {metrics.map((metric) => (
        <StripCard key={metric.id} metric={metric} onOpenKpis={onOpenKpis} />
      ))}
    </div>
  );
}
